import { useEffect, useMemo, useRef, useState } from 'react';

import { findFits } from '../../services/toleranceService';
import { useUnits } from '../../units/UnitContext';
import { displayUnitRange, toCanonical, formatUnitValue, unitLabel } from '../../units/unitConversions';
import { useUnitDrafts, initialUnitDrafts } from '../../units/useUnitDrafts';
import { useUnitText } from '../../units/useUnitText';

const INITIAL_RANGES = {
  clearance: { minimum: '10', maximum: '40' },
  transition: { minimum: '15', maximum: '20' },
  interference: { minimum: '5', maximum: '30' },
};

const MODE_COPY = {
  clearance: { minimum: 'Minimum clearance', maximum: 'Maximum clearance' },
  transition: { minimum: 'Maximum interference', maximum: 'Maximum clearance' },
  interference: { minimum: 'Minimum interference', maximum: 'Maximum interference' },
};

const getRequestParams = ({ diameter, mode, minimum, maximum }, unitSystem = 'metric', messageUnitSystem = unitSystem) => {
  const parsedDiameter = toCanonical(diameter, 'mm', unitSystem);
  const parsedMinimum = toCanonical(minimum, 'um', unitSystem);
  const parsedMaximum = toCanonical(maximum, 'um', unitSystem);
  if (!diameter.trim() || !minimum.trim() || !maximum.trim()) return { status: 'incomplete' };
  if (!Number.isFinite(parsedDiameter) || parsedDiameter < 1 || parsedDiameter > 500) {
    return { status: 'invalid', message: `Enter a nominal diameter from ${displayUnitRange(1, 500, 'mm', messageUnitSystem)}.` };
  }
  if (!Number.isFinite(parsedMinimum) || !Number.isFinite(parsedMaximum) || parsedMinimum < 0 || parsedMaximum < 0) {
    return { status: 'invalid', message: 'Fit limits must be nonnegative numbers.' };
  }
  if (mode !== 'transition' && parsedMinimum > parsedMaximum) {
    return { status: 'invalid', message: 'The minimum limit cannot exceed the maximum limit.' };
  }
  const params = { system: 'ISO286', diameter: parsedDiameter, mode };
  if (mode === 'clearance') {
    params.minimumClearanceUm = parsedMinimum;
    params.maximumClearanceUm = parsedMaximum;
  } else if (mode === 'interference') {
    params.minimumInterferenceUm = parsedMinimum;
    params.maximumInterferenceUm = parsedMaximum;
  } else {
    params.maximumInterferenceUm = parsedMinimum;
    params.maximumClearanceUm = parsedMaximum;
  }
  return { status: 'valid', params };
};

const formatRange = (mode, values, unitSystem) => {
  const n = value => formatUnitValue(value, 'um', unitSystem);
  const unit = unitLabel('um', unitSystem);
  if (mode === 'transition') {
    return `${n(values.maximumInterferenceUm)} ${unit} interference / ${n(values.maximumClearanceUm)} ${unit} clearance`;
  }
  const prefix = mode === 'clearance' ? 'Clearance' : 'Interference';
  return `${prefix} ${n(values[`minimum${prefix}Um`])}–${n(values[`maximum${prefix}Um`])} ${unit}`;
};

const FitFinder = ({ onSelect }) => {
  const { unitSystem } = useUnits();
  const [diameter, setDiameter, canonicalDiameter] = useUnitText('25', 'mm', unitSystem);
  const [mode, setMode] = useState('clearance');
  const [ranges, setRanges] = useState(() => initialUnitDrafts(INITIAL_RANGES, { 'clearance.minimum': 'um', 'clearance.maximum': 'um', 'transition.minimum': 'um', 'transition.maximum': 'um', 'interference.minimum': 'um', 'interference.maximum': 'um' }, unitSystem));
  const canonicalRange = useUnitDrafts(ranges, setRanges, { 'clearance.minimum': 'um', 'clearance.maximum': 'um', 'transition.minimum': 'um', 'transition.maximum': 'um', 'interference.minimum': 'um', 'interference.maximum': 'um' }, unitSystem);
  const [response, setResponse] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(null);
  const currentRange = ranges[mode];
  const parsed = useMemo(
    () => getRequestParams({ diameter: diameter ? String(canonicalDiameter(diameter)) : diameter, mode, minimum: currentRange.minimum ? String(canonicalRange(`${mode}.minimum`, currentRange.minimum, 'um')) : currentRange.minimum, maximum: currentRange.maximum ? String(canonicalRange(`${mode}.maximum`, currentRange.maximum, 'um')) : currentRange.maximum }, 'metric', unitSystem),
    [canonicalDiameter, canonicalRange, diameter, mode, currentRange, unitSystem]
  );

  useEffect(() => () => requestRef.current?.abort(), []);

  const updateRange = (field, value) => setRanges((current) => ({
    ...current,
    [mode]: { ...current[mode], [field]: value },
  }));

  const submit = async (event) => {
    event.preventDefault();
    if (parsed.status !== 'valid') {
      setMessage(parsed.status === 'invalid' ? parsed.message : 'Complete all Finder fields.');
      return;
    }
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setMessage(null);
    setResponse(null);
    try {
      setResponse(await findFits(parsed.params, { signal: controller.signal }));
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') return;
      if (error?.response?.status === 404) setMessage('No ISO 286 candidates are available for that search.');
      else if (error?.response?.status === 400) setMessage(error?.response?.data?.message || 'Check the Finder values.');
      else setMessage('Fit matches could not be loaded. Please try again.');
    } finally {
      if (requestRef.current === controller) setLoading(false);
    }
  };

  return (
    <section className="fitFinder" aria-labelledby="fit-finder-title">
      <header className="fitSectionHeading">
        <p>Search verified combinations</p>
        <h2 id="fit-finder-title">Fit Finder</h2>
        <span>Describe the fit range you need. Finder checks the ISO 286 tolerance records available at that diameter.</span>
      </header>
      <form onSubmit={submit} noValidate>
        <fieldset className="fitFinderModes">
          <legend>Fit type</legend>
          <div className="fitSegmented">
            {Object.keys(MODE_COPY).map((value) => (
              <button key={value} type="button" className={mode === value ? 'isSelected' : ''} aria-pressed={mode === value} onClick={() => setMode(value)}>
                {value[0].toUpperCase() + value.slice(1)}
              </button>
            ))}
          </div>
        </fieldset>
        <div className="fitFinderInputs">
          <label htmlFor="finder-diameter"><span>Nominal diameter</span><div className="fitInputWithUnit"><input id="finder-diameter" aria-label="Finder nominal diameter" inputMode="decimal" value={diameter} onChange={(event) => setDiameter(event.target.value)} /><span>{unitLabel('mm', unitSystem)}</span></div></label>
          <label htmlFor="finder-minimum"><span>{MODE_COPY[mode].minimum}</span><div className="fitInputWithUnit"><input id="finder-minimum" inputMode="decimal" value={currentRange.minimum} onChange={(event) => updateRange('minimum', event.target.value)} /><span>{unitLabel('um', unitSystem)}</span></div></label>
          <label htmlFor="finder-maximum"><span>{MODE_COPY[mode].maximum}</span><div className="fitInputWithUnit"><input id="finder-maximum" inputMode="decimal" value={currentRange.maximum} onChange={(event) => updateRange('maximum', event.target.value)} /><span>{unitLabel('um', unitSystem)}</span></div></label>
        </div>
        <button className="fitFinderSubmit" type="submit" disabled={loading}>{loading ? 'Finding fits…' : 'Find Fits'}</button>
      </form>
      <div className="fitFinderStatus" aria-live="polite">
        {message && <p role={message.includes('could not') ? 'alert' : 'status'}>{message}</p>}
        {response?.matchStrategy === 'near' && <p>Exact containment was not available. Showing the closest ISO 286 matches ranked by total boundary mismatch.</p>}
      </div>
      {response?.results?.length > 0 && (
        <ol className="fitFinderResults" aria-label="Fit Finder results">
          {response.results.map((result) => (
            <li key={`${result.hole}/${result.shaft}`}>
              <article>
                <header><div><strong>{result.designation}</strong><span>{result.matchType === 'full' ? 'Full match' : `Near match · ${formatUnitValue(result.mismatchUm, 'um', unitSystem)} ${unitLabel('um', unitSystem)} mismatch`}</span></div><div className="fitFinderBadges">{result.preferred && <span>Preferred</span>}{result.recommended && <span>Recommended</span>}{!result.recommended && <span>ISO 286 match</span>}</div></header>
                <dl><div><dt>Actual range</dt><dd>{formatRange(mode, result, unitSystem)}</dd></div><div><dt>Requested range</dt><dd>{formatRange(mode, response.request, unitSystem)}</dd></div></dl>
                <button type="button" onClick={() => onSelect({ diameter: response.diameter, hole: result.hole, shaft: result.shaft })}>Use this fit</button>
              </article>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export { getRequestParams };
export default FitFinder;
